import { isSuited, tileNumber, NUM_TILE_TYPES, Tile } from './tiles';
import { toCounts, countsToTiles } from './hand';
import { Group } from './decompose';

/** 묶음을 구성하는 실제 타일(오름차순). */
export function groupTiles(g: Group): Tile[] {
  if (g.type === 'koutsu') return [g.tile, g.tile, g.tile];
  if (g.type === 'shuntsu') return [g.tile, g.tile + 1, g.tile + 2];
  return [g.tile, g.tile]; // pair
}

export interface BestGroups {
  groups: Group[]; // 완성 면자(순자·각자) + 머리(대자, 최대 1개)
  rest: Tile[]; // 완성 묶음에 속하지 않은 나머지(오름차순)
}

/** 표준형 기준, 완성 면자를 최대화하는 분해 하나의 완성 묶음과 나머지를 돌려준다.
 *  점수 = 면자*10 + (머리 있으면 1). 면자 수 우선, 동점이면 머리 보유 우선.
 *  순수·결정적. 사람 손패 표시용(한 렌더에 한 번 호출). */
export function bestGroups(tiles: Tile[]): BestGroups {
  const counts = toCounts(tiles);
  let best: { score: number; groups: Group[] } = { score: -1, groups: [] };

  function dfs(idx: number, melds: number, hasPair: boolean, chosen: Group[]): void {
    while (idx < NUM_TILE_TYPES && counts[idx] === 0) idx++;
    if (idx === NUM_TILE_TYPES) {
      const score = melds * 10 + (hasPair ? 1 : 0);
      if (score > best.score) best = { score, groups: chosen.slice() };
      return;
    }
    // 각자(刻子)
    if (counts[idx] >= 3) {
      counts[idx] -= 3; chosen.push({ type: 'koutsu', tile: idx });
      dfs(idx, melds + 1, hasPair, chosen);
      chosen.pop(); counts[idx] += 3;
    }
    // 순자(順子)
    if (isSuited(idx) && tileNumber(idx) <= 7 && counts[idx + 1] > 0 && counts[idx + 2] > 0) {
      counts[idx]--; counts[idx + 1]--; counts[idx + 2]--;
      chosen.push({ type: 'shuntsu', tile: idx });
      dfs(idx, melds + 1, hasPair, chosen);
      chosen.pop();
      counts[idx]++; counts[idx + 1]++; counts[idx + 2]++;
    }
    // 머리(대자) — 최대 1개
    if (!hasPair && counts[idx] >= 2) {
      counts[idx] -= 2; chosen.push({ type: 'pair', tile: idx });
      dfs(idx, melds, true, chosen);
      chosen.pop(); counts[idx] += 2;
    }
    // 이 패 한 장은 남김(고립)
    counts[idx]--;
    dfs(idx, melds, hasPair, chosen);
    counts[idx]++;
  }

  dfs(0, 0, false, []);

  const remain = toCounts(tiles);
  for (const g of best.groups) for (const t of groupTiles(g)) remain[t]--;
  return { groups: best.groups, rest: countsToTiles(remain) };
}

/** 표시용 정렬: 완성 묶음 타일을 붙여서 앞에, 나머지는 오름차순으로 뒤에. */
export function groupAwareSort(tiles: Tile[]): Tile[] {
  const { groups, rest } = bestGroups(tiles);
  const out: Tile[] = [];
  for (const g of groups) out.push(...groupTiles(g));
  out.push(...rest.slice().sort((a, b) => a - b));
  return out;
}
